import {
  AsYouType,
  type CountryCode,
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from 'libphonenumber-js';

export type { CountryCode } from 'libphonenumber-js';

/** Телефон хранится в E.164: `+79991234567`. */
export type PhoneValue = string;

export const DEFAULT_COUNTRY: CountryCode = 'RU';

/** Страны с кодом +7: у них «8» в начале — выход на междугороднюю линию, а не цифра номера. */
const TRUNK_EIGHT = new Set<CountryCode>(['RU', 'KZ']);

/**
 * Приведение ввода к международному виду `+<цифры>`: `8 999…` → `+7 999…` (для +7),
 * `00 49…` → `+49…`, номер без кода страны — с кодом выбранной страны.
 */
export function toInternationalDigits(text: string, country: CountryCode): string {
  const trimmed = text.trim();
  const digits = trimmed.replace(/\D/g, '');
  if (digits === '') return trimmed.startsWith('+') ? '+' : '';
  if (trimmed.startsWith('+')) return `+${digits}`;
  if (digits.startsWith('00')) return `+${digits.slice(2)}`;
  if (TRUNK_EIGHT.has(country) && digits.startsWith('8')) return `+7${digits.slice(1)}`;
  if (TRUNK_EIGHT.has(country) && digits.startsWith('7') && digits.length === 11) return `+${digits}`;
  return `+${getCountryCallingCode(country)}${digits}`;
}

/** `wrong_length` — цифр больше или меньше, чем бывает в номерах этой страны. */
export type PhoneProblem = 'too_short' | 'too_long' | 'wrong_length' | 'invalid';

export interface ParsedPhone {
  readonly e164: PhoneValue | null;
  /** Страна по коду номера; для +7 — Россия, пока номер не укажет на Казахстан. */
  readonly country: CountryCode | null;
  readonly problem: PhoneProblem | null;
}

/** Разбор номера: E.164, страна и что не так (коротко, длинно, такого номера не бывает). */
export function parsePhoneInput(text: string, country: CountryCode = DEFAULT_COUNTRY): ParsedPhone {
  const international = toInternationalDigits(text, country);
  if (international.length <= 1) return { e164: null, country: null, problem: 'too_short' };
  const length = validatePhoneNumberLength(international);
  if (length === 'TOO_SHORT' || length === 'NOT_A_NUMBER')
    return { e164: null, country: null, problem: 'too_short' };
  if (length === 'TOO_LONG') return { e164: null, country: null, problem: 'too_long' };
  if (length === 'INVALID_LENGTH') return { e164: null, country: null, problem: 'wrong_length' };
  const phone = parsePhoneNumberFromString(international);
  if (!phone?.isValid()) return { e164: null, country: phone?.country ?? null, problem: 'invalid' };
  return { e164: phone.number, country: phone.country ?? null, problem: null };
}

/** Российская маска для номеров +7: `+7 (999) 123-45-67`, в том числе частично набранных. */
function formatSeven(national: string): string {
  const d = national.slice(0, 10);
  let out = '+7';
  if (d.length > 0) out += ` (${d.slice(0, 3)}`;
  if (d.length >= 3) out += ')';
  if (d.length > 3) out += ` ${d.slice(3, 6)}`;
  if (d.length > 6) out += `-${d.slice(6, 8)}`;
  if (d.length > 8) out += `-${d.slice(8, 10)}`;
  return out;
}

/** Как номер стоит в поле: +7 — по российской маске, остальные — международный формат. */
export function formatPhoneInput(value: PhoneValue): string {
  if (value.startsWith('+7')) return formatSeven(value.slice(2));
  return parsePhoneNumberFromString(value)?.formatInternational() ?? value;
}

/** Страна, которую подсказывают набранные цифры (для флага), или null. */
export function detectCountry(international: string): CountryCode | null {
  if (international.startsWith('+7')) {
    const kz = parsePhoneNumberFromString(international);
    return kz?.country === 'KZ' ? 'KZ' : 'RU';
  }
  const typing = new AsYouType();
  typing.input(international);
  return typing.getCountry() ?? null;
}

/**
 * Маска при вводе: номер переформатируется по мере набора, курсор остаётся после той же цифры.
 * Возвращает и страну, если её можно понять по коду.
 */
export function formatPhoneLive(
  text: string,
  caret: number,
  country: CountryCode,
): { text: string; caret: number; country: CountryCode | null } {
  const international = toInternationalDigits(text, country);
  if (international === '' || international === '+')
    return { text: international, caret: international.length, country: null };
  const next = international.startsWith('+7')
    ? formatSeven(international.slice(2))
    : new AsYouType().input(international);
  // Сколько цифр было до курсора — после стольких же цифр курсор и встанет.
  // «8» в начале для +7 превращается в «+7», поэтому считаем цифры международного вида.
  const typedBefore = text.slice(0, caret).replace(/\D/g, '').length;
  const shift = international.replace(/\D/g, '').length - text.replace(/\D/g, '').length;
  const digitsBefore = Math.max(0, typedBefore + shift);
  let seen = 0;
  let position = 0;
  while (position < next.length && seen < digitsBefore) {
    if (/\d/.test(next[position] ?? '')) seen += 1;
    position += 1;
  }
  if (digitsBefore === 0) position = next.startsWith('+') ? 1 : 0;
  return { text: next, caret: position, country: detectCountry(international) };
}

export interface CountryOption {
  readonly code: CountryCode;
  readonly name: string;
  readonly callingCode: string;
}

/** Все страны с названиями по-русски, от частых для студии к остальным по алфавиту. */
export function phoneCountries(popular: readonly CountryCode[] = []): CountryOption[] {
  const names = new Intl.DisplayNames(['ru'], { type: 'region' });
  const all = getCountries().map((code) => ({
    code,
    name: names.of(code) ?? code,
    callingCode: getCountryCallingCode(code),
  }));
  const rank = (code: CountryCode) => {
    const index = popular.indexOf(code);
    return index === -1 ? popular.length : index;
  };
  return all.sort((a, b) => rank(a.code) - rank(b.code) || a.name.localeCompare(b.name, 'ru'));
}

/** Ссылки действий: позвонить и написать в WhatsApp. */
export const phoneLinks = (value: PhoneValue) => ({
  call: `tel:${value}`,
  whatsapp: `https://wa.me/${value.slice(1)}`,
});
